import React, { useEffect, useMemo, useState } from "react";
import sortBy from "lodash/sortBy";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client/react";

import useAuth from "../../../hooks/useAuth";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../../constants/graphql/queries";
import { getInitClientId, getProjectsList } from "../../../layouts/ProjectLayout/FilterNav/index";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "../../../utils/projectOptionLabels";
import AutoExpandEntitySelect from "./AutoExpandEntitySelect";
import ActionsModal from "./ActionsModal";

export default function FilterClientAndProject({
  onSelectProject,
  onSelectClient,
  clientId,
  projectId,
  md = 7,
  hideActions,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { data: { clients = [] } = {} } = useQuery(CLIENTS_AND_PROJECTS_QUERY, {
    fetchPolicy: "cache-first",
  });
  const initClientId = clientId
    ? clients.find(client => client.id === clientId)?.id || "null"
    : getInitClientId(clients, projectId);
  const [selectedClient, setSelectedClient] = useState(initClientId);

  const clientList = sortBy(
    clients.map(({ id, name }) => ({ id, name })),
    "name"
  ).filter(v => user.clientIds.includes(v.id));
  const projectList = getProjectsList(clients, selectedClient, user);
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);
  const showDuplicateProjectClientNames = selectedClient === "null";

  const projectClientLabel = option =>
    showDuplicateProjectClientNames
      ? getProjectClientLabel(option, duplicateNameCounts, showClientNameInProjectList)
      : "";

  const projectOptionLabel = option =>
    showDuplicateProjectClientNames
      ? getProjectOptionLabel(option, duplicateNameCounts, showClientNameInProjectList)
      : option?.name || "";

  const isClearButtonClick = (reason, event) =>
    reason === "clear" && ["click", "mousedown"].includes(event?.type);

  const handleChangeClient = (item, reason, event) => {
    const clientId = isClearButtonClick(reason, event) ? "null" : item?.id;
    setSelectedClient(clientId);
    if (!onSelectClient) return;
    if (clientId) onSelectClient(clientId);
  };

  const handleChangeProject = (projectItem, reason, event) => {
    const projectId = isClearButtonClick(reason, event) ? "all" : projectItem?.id || null;
    onSelectProject({
      clientId: selectedClient,
      projectId,
    });
  };

  useEffect(() => {
    setSelectedClient(initClientId);
  }, [initClientId]);

  return (
    <Grid container justify="space-between" alignItems="center" spacing={1}>
      <Grid s alignItems="stretch" size={{ xs: 12, md: md }}>
        <Grid container spacing={1.5}>
          <Grid size={6}>
            <AutoExpandEntitySelect
              fullWidth
              label={settings.filter_field1}
              name="client"
              value={selectedClient}
              suggestions={[{ id: "null", name: "All" }, ...clientList]}
              onChange={handleChangeClient}
              multiline
              maxRows={3}
            />
          </Grid>
          <Grid size={6}>
            <AutoExpandEntitySelect
              fullWidth
              disabled={!selectedClient && selectedClient !== "null"}
              allowEmpty
              name="project"
              label={settings.filter_field2}
              value={projectId}
              suggestions={projectList}
              onChange={handleChangeProject}
              getOptionLabel={projectOptionLabel}
              renderOption={(props, option) => {
                const optionProps = { ...props };
                delete optionProps.key;
                const clientLabel = projectClientLabel(option);
                return (
                  <li {...optionProps} key={option.id}>
                    {option.name}
                    {clientLabel ? (
                      <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                        {" - "}
                        {clientLabel}
                      </Typography>
                    ) : null}
                  </li>
                );
              }}
              multiline
              maxRows={3}
            />
          </Grid>
        </Grid>
      </Grid>
      {!hideActions && (
        <Grid container spacing={2} justify="flex-end" size={{ xs: 12, md: 5 }}>
          <Grid size={12}>
            <ActionsModal />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
